import React, { useEffect, useState } from "react";
import { getReminderAccordingtoDate } from "./services/services";
import * as XLSX from "xlsx";

const CreatedAtReminderList = () => {
  const [selectedDate, setSelectedDate] = useState(""); // date string in YYYY-MM-DD
  const [allReminders, setAllReminders] = useState([]);
  const [filteredReminders, setFilteredReminders] = useState([]);
  const [loading, setLoading] = useState(false);

  // New states for search + filters
  const [search, setSearch] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [clinicFilter, setClinicFilter] = useState("");
  const [placeFilter, setPlaceFilter] = useState("");

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setSelectedDate(today);
  }, []);

  useEffect(() => {
    setLoading(true);
    getReminderAccordingtoDate({
      data: { filter: "" },
      page: 0,
      pageSize: 10000,
      order: [["createdAt", "ASC"]],
    })
      .then((res) => {
        console.log(res);
        setAllReminders(res?.data?.data?.rows || []);
      })
      .catch((err) => {
        console.error("Error fetching reminders:", err);
        setAllReminders([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedDate) {
      setFilteredReminders([]);
      return;
    }

    const filtered = allReminders.filter((r) => {
      // Convert DB date to "YYYY-MM-DD"
      const createdDate = new Date(r.date).toISOString().split("T")[0];
      return createdDate === selectedDate; // both are YYYY-MM-DD now
    });
    console.log(filtered);

    const searchLower = search.toLowerCase();
    let searched = filtered.filter((r) => {
      return (
        r.patientName?.toLowerCase().includes(searchLower) ||
        r.patientNumber?.includes(searchLower) ||
        r.disease?.toLowerCase().includes(searchLower) ||
        r.address?.toLowerCase().includes(searchLower) || // 🔍 search by place
        r.fromWhere?.toLowerCase().includes(searchLower) // 🔍 search by clinic
      );
    });

    if (reasonFilter) {
      searched = searched.filter((r) => r.reason === reasonFilter);
    }
    if (clinicFilter) {
      searched = searched.filter(
        (r) => r.fromWhere?.toLowerCase() === clinicFilter.toLowerCase()
      );
    }
    if (placeFilter) {
      searched = searched.filter(
        (r) => r.address?.toLowerCase() === placeFilter.toLowerCase()
      );
    }

    setFilteredReminders(searched);
  }, [
    selectedDate,
    allReminders,
    search,
    reasonFilter,
    clinicFilter,
    placeFilter,
  ]);

  const exportToExcel = () => {
    if (filteredReminders.length === 0) {
      alert("No reminders to export!");
      return;
    }

    const dataForExcel = filteredReminders.map((r) => {
      const visits = Array.isArray(r.nextVisits) ? r.nextVisits : [];
      const nextVisits = visits.length > 0 ? visits.join(", ") : "-";

      return {
        ID: r.id,
        Name: r.patientName,
        Phone: r.patientNumber,
        Age: r.Age,
        Address: r.address,
        "Disease / Concern": r.disease,
        From: r.fromWhere,
        Reason:
          r.reason === "opd"
            ? "OPD"
            : r.reason === "operation"
            ? "Operation"
            : "Revisit",
        "Created At": r.createdAt?.split("T")[0],
        "Next Visits": nextVisits,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reminders");
    XLSX.writeFile(workbook, `reminders_${selectedDate}.xlsx`);
  };

  // Get unique clinic and place values for dropdowns
  const uniqueClinics = [
    
    ...new Set(allReminders.map((r) => r.fromWhere).filter(Boolean)),
  ];
  const uniquePlaces = [
    ...new Set(allReminders.map((r) => r.address).filter(Boolean)),
  ];

  return (
    <div
      className="p-4"
      style={{
        background: "linear-gradient(135deg, #f8fbff, #e0f7fa)",
        minHeight: "100vh",
      }}
    >
      <h3 className="text-primary fw-bold mb-4">
        📅 Check Reminders by Created Date
      </h3>

      {/* Filters Row */}
      <div className="row mb-4 g-3">
        <div className="col-md-2">
          <label className="form-label fw-semibold">Select Created Date</label>
          <input
            type="date"
            className="form-control form-control-lg"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>

        <div className="col-md-3">
          <label className="form-label fw-semibold">Search</label>
          <input
            type="text"
            className="form-control form-control-lg"
            placeholder="🔍 Search by Place,name, phone, disease or clinic"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="col-md-2">
          <label className="form-label fw-semibold">Filter by Reason</label>
          <select
            className="form-select form-select-lg"
            value={reasonFilter}
            onChange={(e) => setReasonFilter(e.target.value)}
          >
            <option value="">All Reasons</option>
            <option value="opd">OPD</option>
            <option value="operation">Operation</option>
            <option value="revisit">Revisit</option>
            <option value="surgerypending">Surgery Pending</option>
          </select>
        </div>

        <div className="col-md-2">
          <label className="form-label fw-semibold">Filter by Clinic</label>
          <select
            className="form-select form-select-lg"
            value={clinicFilter}
            onChange={(e) => setClinicFilter(e.target.value)}
          >
            <option value="">All Clinics</option>
            {uniqueClinics.map((c, i) => (
              <option key={i} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-2">
          <label className="form-label fw-semibold">Filter by Place</label>
          <select
            className="form-select form-select-lg"
            value={placeFilter}
            onChange={(e) => setPlaceFilter(e.target.value)}
          >
            <option value="">All Places</option>
            {uniquePlaces.map((p, i) => (
              <option key={i} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-1 d-flex align-items-end">
          <button className="btn btn-primary w-100" onClick={exportToExcel}>
            📥 Export
          </button>
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : selectedDate ? (
        <div className="card shadow-lg border-0 rounded-4 p-3">
          <h5 className="text-secondary fw-bold mb-3">
            Reminders created on {selectedDate}:
          </h5>

          {filteredReminders.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Age</th>
                    <th>Address</th>
                    <th>Disease / Concern</th>
                    <th>From (Clinic)</th>
                    <th>Reason</th>
                    <th>Created At</th>
                    <th>Next Visits</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReminders.map((r, i) => {
                    const visits = Array.isArray(r.nextVisits)
                      ? r.nextVisits
                      : [];
                    return (
                      <tr key={i}>
                        <td>{r.id}</td>
                        <td>{r.patientName}</td>
                        <td>{r.patientNumber}</td>
                        <td>{r.Age}</td>
                        <td>{r.address}</td>
                        <td>{r.disease}</td>
                        <td>{r.fromWhere}</td>
                        <td>
                          <span
                            className="badge"
                            style={{
                              backgroundColor: "#0d6efd",
                              color: "white",
                              padding: "6px 12px",
                              borderRadius: "12px",
                              fontSize: "0.9rem",
                            }}
                          >
                            {r.reason === "opd"
                              ? "OPD"
                              : r.reason === "operation"
                              ? "Operation"
                              : "Revisit"}
                          </span>
                        </td>
                        <td>{r.createdAt?.split("T")[0]}</td>
                        <td>
                          {visits.length > 0
                            ? visits.map((d, idx) => (
                                <span
                                  key={idx}
                                  className="badge bg-info text-dark me-1"
                                >
                                  {d.split("T")[0]}
                                </span>
                              ))
                            : "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted">
              No reminders found for this created date.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default CreatedAtReminderList;
